import React from 'react';
import { AlertTriangle, RotateCcw, Home, MessageSquare } from 'lucide-react';

/**
 * Last-resort error boundary for the customer dashboard.
 *
 * Without this, any render error inside the (very large) DashboardOverview
 * tree unmounts everything above it, leaving the customer staring at a
 * blank white page (with the canvas-confetti layer still alive, since it
 * mounts at <body> level outside the React tree). That has happened more
 * than once during the ETC Usage Inquiry Service (MEISAI) wizard transitions; this guard makes that
 * failure mode visible and recoverable.
 */
export default class DashboardErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, errorMessage: (error && error.message) || 'Unknown error' };
  }

  componentDidCatch(error, info) {
    // Best-effort console log — never throw from inside the boundary.
    try {
      // eslint-disable-next-line no-console
      console.error('[DashboardErrorBoundary]', error, info?.componentStack);
    } catch { /* ignore */ }
  }

  handleReload = () => {
    try { localStorage.removeItem('jtr_setup_confetti_shown'); } catch { /* ignore */ }
    // Hard reload bypasses the broken React state.
    window.location.reload();
  };

  handleHome = () => {
    window.location.assign('/');
  };

  handleSupport = () => {
    window.location.assign('/contact');
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4 py-10" data-testid="dashboard-error-boundary">
        <div className="max-w-lg w-full bg-white rounded-2xl border border-rose-200 shadow-sm p-7">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-rose-600" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Something hit a snag</h2>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                Your dashboard couldn't finish drawing. Your data is safe — this is a
                display-side hiccup. Reloading the page almost always recovers it.
              </p>
            </div>
          </div>

          {this.state.errorMessage && (
            <div className="mt-3 px-3 py-2 rounded-md bg-rose-50/60 border border-rose-100 text-[11px] font-mono text-rose-800 break-all" data-testid="dashboard-error-message">
              {this.state.errorMessage}
            </div>
          )}

          <div className="mt-5 flex flex-col sm:flex-row gap-2">
            <button
              onClick={this.handleReload}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold rounded-lg transition-colors"
              data-testid="dashboard-error-reload"
            >
              <RotateCcw className="w-4 h-4" strokeWidth={2.5} /> Reload page
            </button>
            <button
              onClick={this.handleHome}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 text-sm font-bold rounded-lg"
              data-testid="dashboard-error-home"
            >
              <Home className="w-4 h-4" strokeWidth={2} /> Home
            </button>
            <button
              onClick={this.handleSupport}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 text-sm font-bold rounded-lg"
              data-testid="dashboard-error-support"
            >
              <MessageSquare className="w-4 h-4" strokeWidth={2} /> Support
            </button>
          </div>

          <p className="mt-4 text-[11px] text-slate-400 leading-relaxed">
            If reloading doesn't work, please contact support and mention the error
            message above so we can investigate.
          </p>
        </div>
      </div>
    );
  }
}
